import React, { useState } from "react";

/**
 * Add2Wallet.link landing page
 * Brand: Mobile.Digital (deep forest green + lime accent)
 *
 * Self-contained: all styles are injected via the <style> block below, and the
 * wallet-pass visuals are built in CSS/SVG so there are no external image
 * dependencies.
 */

const CONTACT_URL = "https://www.mobile.digital/contact";
const CASE_STUDY_URL =
  "https://www.linkedin.com/pulse/from-guidebook-digital-concierge-how-mr-mrs-smith-extended-stuckey-j7idc/";
const LOGIN_URL = "https://app.intouch.digital/login";
const PRODUCT_URL = "https://www.mobile.digital/products/add2wallet";
const API_URL = "https://www.mobile.digital/products/api-webhook-integration";
const ABOUT_URL = "https://www.mobile.digital/about-us";
const PRIVACY_URL = "https://www.mobile.digital/privacy-policy";
const TERMS_URL = "https://www.mobile.digital/terms-and-conditions";

const FEATURES = [
  {
    title: "One pass, every wallet",
    body:
      "Generate native Apple Wallet, Google Wallet and Samsung Wallet passes from a single source. No separate builds, no app to ship.",
    icon: "wallet",
  },
  {
    title: "Delivered by link",
    body:
      "Every pass lives behind a unique, parameterised link. Drop it in an email, SMS, QR code or button, and customers just tap Add2Wallet.",
    icon: "link",
  },
  {
    title: "On-brand by design",
    body:
      "Your colours, your logo, your barcode. Passes look like they were built by your team, because they were.",
    icon: "brush",
  },
  {
    title: "Live loyalty & points",
    body:
      "Show balances, tiers and rewards that update on the lock screen the moment they change. Keep customers coming back.",
    icon: "points",
  },
  {
    title: "Push updates anytime",
    body:
      "Change a pass after it's issued, whether it's a new offer, a gate change or a top-up, and it lands straight on the lock screen.",
    icon: "bell",
  },
  {
    title: "Analytics that matter",
    body:
      "Track installs, taps and redemptions in one place so you can see exactly what your passes are driving.",
    icon: "chart",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Design your pass",
    body:
      "Start from a template or your brand kit. Set colours, logo, fields and barcode. No design tools required.",
  },
  {
    n: "02",
    title: "Share the link",
    body:
      "We generate a unique link for every pass. Send it however you already reach customers: email, SMS, QR or web.",
  },
  {
    n: "03",
    title: "Customers tap to add",
    body:
      "One tap adds your pass to Apple, Google or Samsung Wallet. From then on you're on the lock screen, and one push away.",
  },
];

const USE_CASES = [
  { title: "Loyalty & membership", body: "Digital cards with live points, tiers and perks." },
  { title: "Coupons & offers", body: "Time-limited deals that ping the lock screen." },
  { title: "Event tickets", body: "Scannable entry passes with real-time updates." },
  { title: "Gift cards", body: "Branded, trackable balances customers keep handy." },
  { title: "Bookings & appointments", body: "Reminders that travel with the customer." },
  { title: "Boarding & access", body: "Secure passes for venues, transit and workplaces." },
];

/* ---------- tiny inline icon set (stroke = currentColor) ---------- */
function Icon({ name }: { name: string }) {
  const common = {
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "wallet":
      return (
        <svg {...common}>
          <rect x="3" y="6" width="18" height="13" rx="2.5" />
          <path d="M3 9h18" />
          <circle cx="16.5" cy="13" r="1.4" />
        </svg>
      );
    case "link":
      return (
        <svg {...common}>
          <path d="M10 13a4 4 0 0 0 5.7.4l2.6-2.6A4 4 0 1 0 12.7 5.6L11.3 7" />
          <path d="M14 11a4 4 0 0 0-5.7-.4L5.7 13.2a4 4 0 1 0 5.6 5.6L12.7 17" />
        </svg>
      );
    case "brush":
      return (
        <svg {...common}>
          <path d="M4 20c1.5-2 2-4 4-4a3 3 0 0 1 0 6c-2 0-3-1-4-2Z" />
          <path d="M14.5 4.5 20 10l-8 6-3-3 5.5-8.5Z" />
        </svg>
      );
    case "points":
      return (
        <svg {...common}>
          <path d="M12 3l2.5 5.2 5.7.8-4.1 4 1 5.6L12 21l-5.1 2.6 1-5.6-4.1-4 5.7-.8L12 3Z" />
        </svg>
      );
    case "bell":
      return (
        <svg {...common}>
          <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" />
          <path d="M10 20a2 2 0 0 0 4 0" />
        </svg>
      );
    case "chart":
      return (
        <svg {...common}>
          <path d="M4 20V4M4 20h16" />
          <rect x="7" y="12" width="3" height="5" />
          <rect x="12" y="8" width="3" height="9" />
          <rect x="17" y="14" width="3" height="3" />
        </svg>
      );
    default:
      return null;
  }
}

/* ---------- MobileDigital intouch icon (real brand asset) ---------- */
function BrandMark({ size = 30 }: { size?: number }) {
  return (
    <img
      src="/images/md_intouch_icon.png"
      alt=""
      width={size}
      height={size}
      aria-hidden="true"
      style={{ borderRadius: Math.round(size * 0.26), display: "block" }}
    />
  );
}

/* ---------- a wallet pass mockup, pure CSS/SVG ---------- */
function PassCard({
  tag = "STORE CARD",
  brand = "SneakerHub",
  title,
  points,
  tint,
}: {
  tag?: string;
  brand?: string;
  title?: string;
  points?: string;
  tint?: string;
}) {
  return (
    <div
      className="pass"
      style={tint ? ({ "--pass-accent": tint } as React.CSSProperties) : undefined}
    >
      <div className="pass-top">
        <span className="pass-logo">
          <BrandMark size={30} />
        </span>
        <span className="pass-tag">{tag}</span>
      </div>
      <div className="pass-brand">{brand}</div>
      {points != null && (
        <div className="pass-points">
          <span className="pass-points-num">{points}</span>
          <span className="pass-points-label">points</span>
        </div>
      )}
      {title && <div className="pass-title">{title}</div>}
      <div className="pass-barcode" aria-hidden="true">
        {Array.from({ length: 42 }).map((_, i) => (
          <i key={i} style={{ width: (i % 5) + 1 }} />
        ))}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="a2w">
      <style>{CSS}</style>

      {/* ---------------- NAV ---------------- */}
      <header className="nav">
        <div className="container nav-inner">
          <a className="brand" href="#top" aria-label="Add2Wallet.link home">
            <BrandMark size={28} />
            <span className="brand-name">
              Add2Wallet<span className="brand-suffix">.link</span>
            </span>
          </a>

          <nav className={`nav-links ${menuOpen ? "open" : ""}`}>
            <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#how" onClick={() => setMenuOpen(false)}>How it works</a>
            <a href="#use-cases" onClick={() => setMenuOpen(false)}>Use cases</a>
            <a href="#developers" onClick={() => setMenuOpen(false)}>Developers</a>
            <a className="nav-cta-ghost" href={LOGIN_URL}>Log in</a>
            <a className="btn btn-primary btn-sm" href={CONTACT_URL}>Book a demo</a>
          </nav>

          <button
            className="nav-toggle"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ---------------- HERO ---------------- */}
      <section className="hero" id="top">
        <div className="container hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">
              <span className="dot" /> Apple, Google &amp; Samsung Wallet, done right
            </span>
            <h1>
              Put your brand in your customer's <span className="hl">wallet.</span>
            </h1>
            <p className="lead">
              Add2Wallet.link turns loyalty cards, coupons, tickets and memberships into
              native Apple&nbsp;Wallet, Google&nbsp;Wallet and Samsung&nbsp;Wallet passes,
              delivered through a single link. No app to build, no dev team required.
            </p>
            <div className="hero-cta">
              <a className="btn btn-primary" href={CONTACT_URL}>Book a demo</a>
              <a className="btn btn-ghost" href={PRODUCT_URL}>Explore the platform →</a>
            </div>
            <div className="hero-meta">
              <span>✓ Live in minutes</span>
              <span>✓ No customer app</span>
              <span>✓ Updates on the lock screen</span>
            </div>
          </div>

          <div className="hero-art" aria-hidden="true">
            <div className="phone">
              <div className="phone-notch" />
              <div className="phone-screen">
                <div className="phone-time">9:41</div>
                <div className="phone-lock">Wallet</div>
                <PassCard brand="SneakerHub" tag="STORE CARD" points="2,500" />
                <PassCard brand="Aroma Coffee" tag="LOYALTY" title="Buy 9, get 1 free" tint="#0E4A38" />
              </div>
            </div>
            <div className="float-pass">
              <PassCard brand="City Gym" tag="MEMBER" title="Active · Gold" tint="#14543f" />
            </div>
            <div className="glow" />
          </div>
        </div>

      </section>

      {/* ---------------- FEATURES ---------------- */}
      <section className="section" id="features">
        <div className="container">
          <div className="section-head">
            <span className="kicker">Everything you need</span>
            <h2>A wallet strategy without the engineering project.</h2>
            <p>
              From first design to live push updates, Add2Wallet.link gives marketing teams
              a direct line to the most valuable screen your customer owns.
            </p>
          </div>
          <div className="feature-grid">
            {FEATURES.map((f) => (
              <div className="feature" key={f.title}>
                <span className="feature-icon"><Icon name={f.icon} /></span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- HOW IT WORKS ---------------- */}
      <section className="section how" id="how">
        <div className="container">
          <div className="section-head">
            <span className="kicker">How it works</span>
            <h2>Three steps to the lock screen.</h2>
          </div>
          <div className="steps">
            {STEPS.map((s) => (
              <div className="step" key={s.n}>
                <span className="step-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- USE CASES ---------------- */}
      <section className="section" id="use-cases">
        <div className="container">
          <div className="section-head">
            <span className="kicker">Use cases</span>
            <h2>One platform, every kind of pass.</h2>
          </div>
          <div className="uc-grid">
            {USE_CASES.map((u) => (
              <div className="uc" key={u.title}>
                <h3>{u.title}</h3>
                <p>{u.body}</p>
                <span className="uc-arrow">→</span>
              </div>
            ))}
          </div>

          {/* featured case study: Mr & Mrs Smith digital concierge */}
          <a
            className="uc-featured"
            href={CASE_STUDY_URL}
            target="_blank"
            rel="noreferrer"
          >
            <div className="uc-featured-copy">
              <span className="uc-featured-kicker">Case study · Travel &amp; hospitality</span>
              <h3>From guidebook to digital concierge: Mr &amp; Mrs Smith</h3>
              <p>
                The luxury hotel collection turned booking confirmations into a
                concierge in the guest's pocket: directions and local tips on
                arrival, location-triggered welcome gifts at check-in, scheduled
                reminders after the stay, and Blacksmith, Silversmith and
                Goldsmith membership cards in Apple Wallet.
              </p>
            </div>
            <span className="uc-featured-cta">Read the story on LinkedIn →</span>
          </a>
        </div>
      </section>

      {/* ---------------- DEVELOPERS ---------------- */}
      <section className="section developers" id="developers">
        <div className="container dev-grid">
          <div className="dev-copy">
            <span className="kicker kicker-lime">Built for developers too</span>
            <h2>A clean API behind every link.</h2>
            <p>
              Under the hood, Add2Wallet.link runs on modern serverless infrastructure.
              Generate and distribute passes programmatically through parameterised
              links, then update them with a single request.
            </p>
            <ul className="dev-list">
              <li>Parameterised links for Apple <span>.pkpass</span>, Google &amp; Samsung Wallet</li>
              <li>Issue and update passes via a REST endpoint</li>
              <li>Serverless, ESM-native, built to scale</li>
            </ul>
            <a className="btn btn-primary" href={API_URL}>Explore the API</a>
          </div>
          <div className="dev-code">
            <div className="code-head">
              <span className="dot r" /><span className="dot y" /><span className="dot g" />
              <span className="code-file">issue-pass.sh</span>
            </div>
            <pre>
{`curl -X POST https://api.add2wallet.link/v1/passes \\
  -H "Authorization: Bearer $A2W_KEY" \\
  -d '{
    "template": "loyalty-card",
    "brand":    "SneakerHub",
    "fields": {
      "points": 2500,
      "member": "Ava R."
    }
  }'

# → { "link": "https://add2wallet.link/p/9fK2xQ" }`}
            </pre>
          </div>
        </div>
      </section>

      {/* ---------------- CTA BAND ---------------- */}
      <section className="cta-band">
        <div className="container cta-inner">
          <h2>Ready to be one tap away?</h2>
          <p>Launch your first branded pass today, or book a walkthrough with our team.</p>
          <div className="hero-cta">
            <a className="btn btn-primary" href={CONTACT_URL}>Book a demo</a>
            <a className="btn btn-ghost-dark" href={PRODUCT_URL}>Learn more →</a>
          </div>
        </div>
      </section>

      {/* ---------------- FOOTER ---------------- */}
      <footer className="footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <a className="brand" href="#top">
              <BrandMark size={26} />
              <span className="brand-name">
                Add2Wallet<span className="brand-suffix">.link</span>
              </span>
            </a>
            <p>Native Apple, Google &amp; Samsung Wallet passes, delivered by link.</p>
            <a
              className="footer-by"
              href="https://www.mobile.digital"
              aria-label="A Mobile.Digital product"
            >
              <img
                src="/images/MobileDigital_BestLogo_transparent.png"
                alt="Mobile.Digital"
              />
            </a>
          </div>
          <div className="footer-col">
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
            <a href="#use-cases">Use cases</a>
            <a href="#developers">Developers</a>
          </div>
          <div className="footer-col">
            <h4>Company</h4>
            <a href={ABOUT_URL}>About</a>
            <a href={CONTACT_URL}>Contact</a>
            <a href="https://www.mobile.digital">Mobile.Digital</a>
          </div>
          <div className="footer-col">
            <h4>Get started</h4>
            <a href={CONTACT_URL}>Book a demo</a>
            <a href={LOGIN_URL}>Log in to intouch</a>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© {new Date().getFullYear()} Add2Wallet.link · A Mobile.Digital™ product</span>
          <div className="footer-legal">
            <a href={PRIVACY_URL}>Privacy</a>
            <a href={TERMS_URL}>Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============================ STYLES ============================ */
const CSS = `
.a2w {
  --ink: #06251B;
  --forest: #0A3B2C;
  --forest-2: #0E4A38;
  --lime: #AEDD1F;
  --lime-2: #C6F04A;
  --cream: #F5F8F1;
  --paper: #FFFFFF;
  --muted: #56675E;
  --line: #E4EAE2;
  --pass-accent: #0A3B2C;

  color: var(--ink);
  background: var(--paper);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  line-height: 1.5;
  text-align: left;
}
.a2w *, .a2w *::before, .a2w *::after { box-sizing: border-box; }
.a2w h1, .a2w h2, .a2w h3, .a2w h4 { margin: 0; line-height: 1.1; letter-spacing: -0.02em; font-weight: 700; }
.a2w p { margin: 0; }
.a2w a { color: inherit; text-decoration: none; }
.a2w .container { width: min(1160px, 92%); margin: 0 auto; }

/* buttons */
.a2w .btn {
  display: inline-flex; align-items: center; justify-content: center;
  gap: .5rem; font-weight: 600; font-size: 1rem;
  padding: .85rem 1.5rem; border-radius: 999px; cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
  border: 1.5px solid transparent; white-space: nowrap;
}
.a2w .btn-sm { padding: .6rem 1.15rem; font-size: .92rem; }
.a2w .btn-primary { background: var(--lime); color: var(--ink); box-shadow: 0 8px 24px -8px rgba(174,221,31,.7); }
.a2w .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 14px 30px -8px rgba(174,221,31,.85); }
.a2w .btn-ghost { background: transparent; color: var(--forest); border-color: var(--line); }
.a2w .btn-ghost:hover { border-color: var(--forest); transform: translateY(-2px); }
.a2w .btn-ghost-dark { background: transparent; color: #fff; border-color: rgba(255,255,255,.4); }
.a2w .btn-ghost-dark:hover { border-color: #fff; transform: translateY(-2px); }

/* nav */
.a2w .nav { position: sticky; top: 0; z-index: 50; background: rgba(255,255,255,.82); backdrop-filter: blur(12px); border-bottom: 1px solid var(--line); }
.a2w .nav-inner { display: flex; align-items: center; justify-content: space-between; height: 70px; }
.a2w .brand { display: inline-flex; align-items: center; gap: .55rem; font-weight: 700; font-size: 1.25rem; }
.a2w .brand-suffix { color: #8FB918; }
.a2w .footer .brand-suffix { color: var(--lime); }
.a2w .nav-links { display: flex; align-items: center; gap: 1.6rem; }
.a2w .nav-links > a { font-weight: 500; color: #2c3a33; font-size: .96rem; }
.a2w .nav-links > a:hover { color: var(--forest); }
.a2w .nav-cta-ghost { color: var(--forest) !important; font-weight: 600 !important; }
.a2w .nav-toggle { display: none; flex-direction: column; gap: 5px; background: none; border: 0; cursor: pointer; padding: 8px; }
.a2w .nav-toggle span { width: 24px; height: 2px; background: var(--ink); border-radius: 2px; }

/* hero */
.a2w .hero { position: relative; background: radial-gradient(1200px 500px at 78% -8%, rgba(174,221,31,.18), transparent 60%), linear-gradient(180deg, var(--cream), #fff 70%); padding-top: 4.5rem; overflow: hidden; }
.a2w .hero-grid { display: grid; grid-template-columns: 1.05fr .95fr; gap: 3rem; align-items: center; }
.a2w .eyebrow { display: inline-flex; align-items: center; gap: .5rem; background: #fff; border: 1px solid var(--line); color: var(--forest); font-weight: 600; font-size: .85rem; padding: .4rem .8rem; border-radius: 999px; }
.a2w .eyebrow .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--lime); box-shadow: 0 0 0 4px rgba(174,221,31,.25); }
.a2w .hero-copy h1 { font-size: clamp(2.5rem, 5.2vw, 4rem); margin: 1.2rem 0 1rem; }
.a2w .hl { color: var(--forest); position: relative; }
.a2w .hl::after { content: ""; position: absolute; left: 0; right: 0; bottom: .06em; height: .32em; background: var(--lime); opacity: .55; border-radius: 3px; z-index: -1; }
.a2w .lead { font-size: 1.18rem; color: var(--muted); max-width: 34rem; }
.a2w .hero-cta { display: flex; gap: .9rem; margin: 1.8rem 0 1.2rem; flex-wrap: wrap; }
.a2w .hero-meta { display: flex; gap: 1.4rem; flex-wrap: wrap; color: var(--forest); font-weight: 600; font-size: .92rem; }

/* hero art */
.a2w .hero-art { position: relative; display: flex; justify-content: center; min-height: 520px; }
.a2w .glow { position: absolute; inset: 8% 12%; background: radial-gradient(circle at 50% 40%, rgba(174,221,31,.35), transparent 62%); filter: blur(10px); z-index: 0; }
.a2w .phone { position: relative; z-index: 2; width: 300px; height: 500px; background: linear-gradient(160deg, var(--ink), var(--forest)); border-radius: 42px; padding: 14px; box-shadow: 0 40px 80px -30px rgba(6,37,27,.6), inset 0 0 0 2px rgba(255,255,255,.06); }
.a2w .phone-notch { position: absolute; top: 16px; left: 50%; transform: translateX(-50%); width: 120px; height: 26px; background: #04140f; border-radius: 0 0 16px 16px; z-index: 3; }
.a2w .phone-screen { height: 100%; border-radius: 30px; background: linear-gradient(180deg, #072019, #0b3527); padding: 44px 16px 16px; overflow: hidden; display: flex; flex-direction: column; gap: 14px; }
.a2w .phone-time { color: #fff; font-weight: 700; font-size: .9rem; }
.a2w .phone-lock { color: rgba(255,255,255,.7); font-size: .78rem; letter-spacing: .12em; text-transform: uppercase; }
.a2w .float-pass { position: absolute; z-index: 3; right: -6px; bottom: 40px; width: 230px; transform: rotate(6deg); filter: drop-shadow(0 24px 40px rgba(6,37,27,.4)); }
.a2w .float-pass .pass { animation: a2w-bob 5s ease-in-out infinite; }
@keyframes a2w-bob { 50% { transform: translateY(-10px); } }

/* pass card */
.a2w .pass { --pass-accent: var(--forest); background: linear-gradient(155deg, #fff, #f2f6ee); border-radius: 18px; padding: 14px 15px 16px; box-shadow: 0 10px 26px -14px rgba(6,37,27,.5); border-top: 5px solid var(--lime); }
.a2w .pass-top { display: flex; align-items: center; justify-content: space-between; }
.a2w .pass-logo { width: 30px; height: 30px; border-radius: 8px; overflow: hidden; display: grid; place-items: center; }
.a2w .pass-tag { font-size: .62rem; letter-spacing: .14em; font-weight: 700; color: var(--muted); }
.a2w .pass-brand { font-weight: 700; font-size: 1.15rem; color: var(--ink); margin-top: 10px; }
.a2w .pass-points { display: flex; align-items: baseline; gap: .35rem; margin-top: 8px; }
.a2w .pass-points-num { font-size: 1.7rem; font-weight: 800; color: var(--pass-accent); letter-spacing: -.02em; }
.a2w .pass-points-label { font-size: .8rem; color: var(--muted); font-weight: 600; }
.a2w .pass-title { margin-top: 8px; color: var(--forest); font-weight: 600; font-size: .92rem; }
.a2w .pass-barcode { display: flex; align-items: flex-end; gap: 2px; height: 40px; margin-top: 14px; padding-top: 12px; border-top: 1px dashed #cfd9c9; }
.a2w .pass-barcode i { display: block; height: 100%; background: #12241c; border-radius: 1px; }

/* sections */
.a2w .section { padding: 5.5rem 0; }
.a2w .section-head { max-width: 42rem; margin-bottom: 3rem; }
.a2w .kicker { display: inline-block; color: var(--forest); font-weight: 700; font-size: .82rem; letter-spacing: .08em; text-transform: uppercase; margin-bottom: .9rem; }
.a2w .kicker-lime { color: var(--lime); }
.a2w .section-head h2 { font-size: clamp(1.9rem, 3.6vw, 2.7rem); }
.a2w .section-head p { color: var(--muted); font-size: 1.1rem; margin-top: 1rem; }

/* features */
.a2w .feature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem; }
.a2w .feature { background: var(--paper); border: 1px solid var(--line); border-radius: 18px; padding: 1.7rem; transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
.a2w .feature:hover { transform: translateY(-4px); box-shadow: 0 24px 44px -28px rgba(6,37,27,.35); border-color: transparent; }
.a2w .feature-icon { display: inline-grid; place-items: center; width: 46px; height: 46px; border-radius: 12px; background: var(--forest); color: var(--lime); margin-bottom: 1.1rem; }
.a2w .feature h3 { font-size: 1.18rem; margin-bottom: .5rem; }
.a2w .feature p { color: var(--muted); font-size: .98rem; }

/* how */
.a2w .how { background: linear-gradient(180deg, #fff, var(--cream)); }
.a2w .steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
.a2w .step { position: relative; background: #fff; border: 1px solid var(--line); border-radius: 18px; padding: 2rem 1.7rem; }
.a2w .step-n { font-size: 2.4rem; font-weight: 800; color: var(--lime); -webkit-text-stroke: 1px var(--forest); }
.a2w .step h3 { margin: .6rem 0 .5rem; font-size: 1.25rem; }
.a2w .step p { color: var(--muted); }

/* use cases */
.a2w .uc-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem; }
.a2w .uc { position: relative; border: 1px solid var(--line); border-radius: 18px; padding: 1.6rem; background: var(--paper); overflow: hidden; transition: transform .18s ease, box-shadow .18s ease; }
.a2w .uc::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--lime); }
.a2w .uc:hover { transform: translateY(-4px); box-shadow: 0 24px 44px -28px rgba(6,37,27,.35); }
.a2w .uc h3 { font-size: 1.12rem; margin-bottom: .4rem; }
.a2w .uc p { color: var(--muted); font-size: .96rem; }
.a2w .uc-arrow { position: absolute; top: 1.5rem; right: 1.4rem; color: var(--forest); font-size: 1.1rem; opacity: 0; transform: translateX(-6px); transition: .18s ease; }
.a2w .uc:hover .uc-arrow { opacity: 1; transform: translateX(0); }

/* featured case study card */
.a2w .uc-featured { display: flex; align-items: flex-end; justify-content: space-between; gap: 2rem; margin-top: 1.2rem; padding: 2.2rem; border-radius: 18px; background: linear-gradient(135deg, var(--ink), var(--forest-2)); color: #fff; transition: transform .18s ease, box-shadow .18s ease; }
.a2w .uc-featured:hover { transform: translateY(-4px); box-shadow: 0 24px 50px rgba(2,45,38,.28); }
.a2w .uc-featured-kicker { display: inline-block; color: var(--lime); font-weight: 800; font-size: .78rem; letter-spacing: .1em; text-transform: uppercase; margin-bottom: .7rem; }
.a2w .uc-featured h3 { font-size: clamp(1.35rem, 2.4vw, 1.8rem); letter-spacing: -.03em; margin-bottom: .6rem; color: #fff; }
.a2w .uc-featured p { color: rgba(255,255,255,.78); line-height: 1.6; max-width: 46rem; margin-bottom: 0; }
.a2w .uc-featured-cta { flex-shrink: 0; color: var(--lime); font-weight: 700; white-space: nowrap; }
@media (max-width: 720px) {
  .a2w .uc-featured { flex-direction: column; align-items: flex-start; padding: 1.6rem; }
}

/* developers */
.a2w .developers { background: linear-gradient(160deg, var(--ink), var(--forest)); color: #fff; }
.a2w .developers .kicker { color: var(--lime); }
.a2w .dev-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; align-items: center; }
.a2w .dev-copy h2 { font-size: clamp(1.9rem, 3.4vw, 2.6rem); color: #fff; }
.a2w .dev-copy > p { color: rgba(255,255,255,.75); font-size: 1.08rem; margin-top: 1rem; }
.a2w .dev-list { list-style: none; padding: 0; margin: 1.6rem 0; display: grid; gap: .7rem; }
.a2w .dev-list li { position: relative; padding-left: 1.8rem; color: rgba(255,255,255,.9); }
.a2w .dev-list li::before { content: "✓"; position: absolute; left: 0; color: var(--ink); background: var(--lime); width: 20px; height: 20px; border-radius: 50%; display: grid; place-items: center; font-size: .72rem; font-weight: 800; top: 2px; }
.a2w .dev-list span { color: var(--lime); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .9em; }
.a2w .dev-code { background: #04140f; border: 1px solid rgba(255,255,255,.1); border-radius: 16px; overflow: hidden; box-shadow: 0 30px 60px -30px rgba(0,0,0,.6); }
.a2w .code-head { display: flex; align-items: center; gap: .5rem; padding: .8rem 1rem; background: rgba(255,255,255,.04); border-bottom: 1px solid rgba(255,255,255,.08); }
.a2w .code-head .dot { width: 11px; height: 11px; border-radius: 50%; }
.a2w .code-head .dot.r { background: #ff5f57; } .a2w .code-head .dot.y { background: #febc2e; } .a2w .code-head .dot.g { background: #28c840; }
.a2w .code-file { margin-left: .6rem; color: rgba(255,255,255,.55); font-size: .82rem; font-family: ui-monospace, monospace; }
.a2w .dev-code pre { margin: 0; padding: 1.3rem; color: #d7e6c9; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .85rem; line-height: 1.7; overflow-x: auto; text-align: left; }

/* cta band */
.a2w .cta-band { padding: 5rem 0; background: radial-gradient(800px 300px at 50% 0%, rgba(174,221,31,.25), transparent 65%), var(--cream); }
.a2w .cta-inner { text-align: center; }
.a2w .cta-inner h2 { font-size: clamp(2rem, 4vw, 3rem); }
.a2w .cta-inner p { color: var(--muted); font-size: 1.15rem; margin: 1rem auto 2rem; max-width: 34rem; }
.a2w .cta-band .btn-ghost-dark { color: var(--forest); border-color: var(--forest); }
.a2w .cta-band .hero-cta { justify-content: center; }

/* footer */
.a2w .footer { background: var(--ink); color: rgba(255,255,255,.72); padding: 4rem 0 2rem; }
.a2w .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 2rem; padding-bottom: 3rem; border-bottom: 1px solid rgba(255,255,255,.1); }
.a2w .footer .brand { color: #fff; margin-bottom: 1rem; }
.a2w .footer-brand p { max-width: 22rem; font-size: .95rem; }
.a2w .footer-by { display: inline-flex; align-items: center; margin-top: 1rem; opacity: .9; transition: opacity .15s ease, transform .15s ease; }
.a2w .footer-by:hover { opacity: 1; transform: translateY(-2px); }
.a2w .footer-by img { height: 46px; width: auto; display: block; }
.a2w .footer-col h4 { color: #fff; font-size: .95rem; margin-bottom: 1rem; }
.a2w .footer-col a { display: block; padding: .35rem 0; color: rgba(255,255,255,.7); font-size: .95rem; }
.a2w .footer-col a:hover { color: var(--lime); }
.a2w .footer-bottom { display: flex; justify-content: space-between; align-items: center; padding-top: 1.5rem; font-size: .85rem; color: rgba(255,255,255,.5); }
.a2w .footer-legal { display: flex; gap: 1.4rem; }
.a2w .footer-legal a:hover { color: var(--lime); }

/* responsive */
@media (max-width: 900px) {
  .a2w .hero-grid { grid-template-columns: 1fr; }
  .a2w .hero-art { min-height: 460px; margin-top: 1rem; }
  .a2w .feature-grid, .a2w .steps, .a2w .uc-grid { grid-template-columns: 1fr 1fr; }
  .a2w .dev-grid { grid-template-columns: 1fr; }
  .a2w .footer-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 620px) {
  .a2w .nav-toggle { display: flex; }
  .a2w .nav-links { position: absolute; top: 70px; left: 0; right: 0; background: #fff; flex-direction: column; align-items: stretch; gap: 0; padding: .5rem 6% 1.2rem; border-bottom: 1px solid var(--line); box-shadow: 0 20px 30px -20px rgba(0,0,0,.2); display: none; }
  .a2w .nav-links.open { display: flex; }
  .a2w .nav-links > a { padding: .8rem 0; border-bottom: 1px solid var(--line); }
  .a2w .nav-links .btn { margin-top: .6rem; }
  .a2w .feature-grid, .a2w .steps, .a2w .uc-grid, .a2w .footer-grid { grid-template-columns: 1fr; }
  .a2w .footer-bottom { flex-direction: column; gap: .8rem; }
  .a2w .float-pass { display: none; }
}
`;
